import React from 'react';
import { X, Plus, Minus, ShoppingBag } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { placeholders } from '../lib/placeholders';
import { navigate } from '../lib/router';

export const CartDrawer: React.FC = () => {
  const { cartItems, removeFromCart, updateQuantity, cartTotal, isCartOpen, setIsCartOpen } = useCart();

  if (!isCartOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/50 animate-fade-in"
        onClick={() => setIsCartOpen(false)}
      />
      <div className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md animate-slide-in-right flex-col border-l border-primary/10 bg-background shadow-2xl">
        <div className="flex items-center justify-between border-b border-primary/10 px-6 py-6">
          <div className="flex items-center gap-2">
            <ShoppingBag className="text-primary" size={24} />
            <h2 className="text-2xl font-bold text-text">Your Cart</h2>
          </div>
          <button
            onClick={() => setIsCartOpen(false)}
            className="text-charcoal/70 transition-colors hover:text-primary"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ShoppingBag size={64} className="mb-4 text-primary/20" />
              <p className="text-lg text-charcoal">Your cart is empty</p>
              <p className="mt-2 text-sm text-charcoal/60">Add a few pieces to begin your order.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {cartItems.map((item) => (
                <div
                  key={item.product.id}
                  className="flex gap-4 rounded-2xl border border-primary/10 bg-white/65 p-4 transition-shadow hover:shadow-[0_16px_36px_rgba(13,13,13,0.08)]"
                >
                  <img
                    src={item.product.image_url || item.product.images?.[0] || placeholders.small}
                    alt={item.product.name}
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <h3 className="mb-1 font-semibold text-text">{item.product.name}</h3>
                    <p className="font-semibold text-primary">₹{item.product.price}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                        className="flex h-8 w-8 items-center justify-center rounded border border-primary/15 text-charcoal transition-colors hover:border-primary hover:bg-primary hover:text-white"
                      >
                        <Minus size={16} />
                      </button>
                      <span className="w-8 text-center font-semibold">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                        className="flex h-8 w-8 items-center justify-center rounded border border-primary/15 text-charcoal transition-colors hover:border-primary hover:bg-primary hover:text-white"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.product.id)}
                    className="text-red-500 hover:text-red-700 transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {cartItems.length > 0 && (
          <div className="space-y-4 border-t border-primary/10 p-6">
            <div className="flex justify-between items-center text-lg">
              <span className="font-semibold text-text">Subtotal:</span>
              <span className="text-xl font-bold text-primary">₹{cartTotal.toFixed(2)}</span>
            </div>
            <button
              onClick={() => {
                setIsCartOpen(false);
                navigate('/checkout');
              }}
              className="btn-primary w-full"
            >
              Proceed to Checkout
            </button>
            <button
              onClick={() => setIsCartOpen(false)}
              className="btn-secondary w-full"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </>
  );
};
